<template>
  <div v-plyr-instance>
    <slot></slot>
  </div>
</template>

<script>
import { bus } from './index'
export default {
  name: 'Plyr',
  props: {
    emit: {
      type: Array,
      required: false,
      default() { return [] }
    },
    trackProgress: {
      type: Object,
      required: false
      // Valid examples:
      // <plyr :track-progress="{ percentComplete: [0.25, 0.5, 0.75, 0.95] }">
      // <plyr :track-progress="{ seconds: [5, 10, 20, 9001] }">
      // Precision with tracking a video's current time is not guaranteed,
      // and seconds will get rounded to a whole number.
    },
    options: {
      type: Object,
      required: false,
      default() {
        return {
          loadSprite: false,
          disableContextMenu: false,
          speed: {
            // 7 settings seems a bit excessive for our purposes
            options: [0.5, 1, 1.25, 1.5, 2],
          },
          quality: {
            default: 720
          },
          controls: [
            'play-large',
            // 'restart',
            // 'rewind',
            'play',
            // 'fast-forward',
            'progress',
            'current-time',
            'mute',
            'volume',
            'captions',
            'settings',
            // 'pip',
            // 'airplay',
            'fullscreen',
          ],

          i18n: {
            restart: gettext('Restart'),
            rewind: gettext('Rewind {seektime}s'),
            play: gettext('Play'),
            pause: gettext('Pause'),
            fastForward: gettext('Forward {seektime}s'),
            seek: gettext('Seek'),
            seekLabel: gettext('{currentTime} of {duration}'),
            played: gettext('Played'),
            buffered: gettext('Buffered'),
            currentTime: gettext('Current time'),
            duration: gettext('Duration'),
            volume: gettext('Volume'),
            mute: gettext('Mute'),
            unmute: gettext('Unmute'),
            enableCaptions: gettext('Enable captions'),
            disableCaptions: gettext('Disable captions'),
            enterFullscreen: gettext('Enter fullscreen'),
            exitFullscreen: gettext('Exit fullscreen'),
            frameTitle: gettext('Player for {title}'),
            captions: gettext('Captions'),
            settings: gettext('Settings'),
            menuBack: gettext('Go back to previous menu'),
            speed: gettext('Speed'),
            normal: gettext('Normal'),
            quality: gettext('Quality'),
            loop: gettext('Loop'),
            start: gettext('Start'),
            end: gettext('End'),
            all: gettext('All'),
            reset: gettext('Reset'),
            disabled: gettext('Disabled'),
            enabled: gettext('Enabled'),
            qualityBadge: {
              2160: '4K',
              1440: 'HD',
              1080: 'HD',
              720: 'HD',
              576: 'SD',
              480: 'SD',
            },
          },
        }
      }
    },
  },

  data() {
    return {
      player: {}
    }
  },

  methods: {
    emitPlayerEvent(e) {
      this.$emit(e.type, e)
    },

    formatTimestamp(ts) {
      // Converts an integer representing seconds into a HH:MM:SS string
      let hours = Math.floor(ts / 60 / 60)
      let minutes = Math.floor(ts / 60) - (hours * 60)
      let seconds = ts % 60

      return `${
        hours > 0
          ? hours.toString().padStart(2, '0') + ':'
          : ''
        }${minutes.toString().padStart(2, '0')}:${parseInt(seconds).toString().padStart(2, '0')}`
    },

    trackVideoProgress() {
      if (!this.player.duration) {
        // Bail just in case the video's duration isn't available,
        // although this should essentially never happen
        return false
      }

      let targetTimes

      if (this.trackProgress.percentComplete) {
        targetTimes = this.trackProgress.percentComplete.map(percentage => {
          return {
            seconds: parseFloat(parseFloat(this.player.duration * percentage).toFixed(2)),
            trackReport: `${parseInt(percentage * 100)}% complete`
          }
        })
      } else if (this.trackProgress.seconds) {
        targetTimes = this.trackProgress.seconds.map(timestamp => {
          return {
            seconds: parseFloat(parseFloat(timestamp).toFixed(2)),
            trackReport: `${this.formatTimestamp(timestamp)} seconds complete`
          }
        })
      } else {
        throw new Error('The plyr component’s trackProgress prop expects an object containing either `percentComplete` or `seconds` as its only key, e.g.: `{ seconds: [10, 25, 150, 200] }`')
      }

      let videoPlayer = this.player

      targetTimes.forEach(ts => {
        // This callback uses a traditional function expression instead of an arrow function
        // so the callback can be named and `removeEventListener` set up more easily
        this.player.on('timeupdate', function checkAndTrack() {
          if (videoPlayer.currentTime > ts.seconds) {
            // console.log('track progress', ts.trackReport, videoPlayer.media.currentSrc)
            window._paq.push(['trackEvent', 'video-event', 'track progress', ts.trackReport, videoPlayer.media.currentSrc])
            // Condition is met; remove this listener so it doesn't firehose all over matomo:
            this.removeEventListener('timeupdate', checkAndTrack)
          }
        })

      })

    },
  },

  created() {
    bus.$on('plyr:playing', (id) => {
      // Don't let two videos play simultaneously; that way lies madness
      if (id !== this.player.id) this.player.pause()
    })
  },

  mounted() {
    const plyr = require('plyr')
    const video = this.$el.querySelector('video')

    if (video.textTracks.length >= 1) {

      // Captions are generally for transcribing a video's content in the same
      // language to someone who might not be able to hear what's going on, and
      // should be set up with `<track kind="captions" ...>`
      //
      // Activating captions should be left as an option for the viewer.
      //
      // Plyr will remember a viewer's last captions selection and keep track
      // of this in localStorage.

      // Subtitles are generally for translating a video's content into
      // a different language, and should be set up with `<track kind="subtitles" ...>`
      //
      // Activating subtitles should happen automatically where possible,
      // which can be done by matching a text track's `kind` and `language`
      // to the language set on the current page (or section, as the case may be).
      //
      // For us, this situation happens most commonly with an English video on
      // a French page that has French subtitles available.
      //
      // Plyr will also remember a viewer's last subtitles selection in localStorage
      // and override any defaults we set with those values instead.

      let textTracks = Object.values(video.textTracks)
      // Find the nearest declared language:
      let parentLang = video.closest('[lang]').getAttribute('lang')

      if (textTracks.find(track => track.kind === 'subtitles' && track.language === parentLang)) {
        // Found a subtitle track matching the page/section's language,
        // so activate it by default:
        this.options['captions'] = {
          active: true,
          language: parentLang,
        }
      }
    }

    this.player = new plyr(video, this.options)

    this.emit.forEach(el => {
      this.player.on(el, this.emitPlayerEvent)
    })

    this.player.on('playing', (e) => {
      bus.$emit('plyr:playing', this.player.id)
      window._paq.push(['trackEvent', 'video-event', 'started playing', this.player.media.currentSrc])
    })
    this.player.on('ended', (e) => {
      bus.$emit('plyr:ended', this.player.media.currentSrc)
      window._paq.push(['trackEvent', 'video-event', 'ended/completed playing', this.player.media.currentSrc])
    })
    this.player.on('error', (e) => {
      window._paq.push(['trackEvent', 'video-event', 'video error', e])
    })

    if (this.trackProgress) {
      this.player.once('loadedmetadata', () => {
        // Now the video's data will have a duration value available,
        // making it possible to track progress
        this.trackVideoProgress()
      })
    }

    this.$nextTick(() => {
      // plyr fires off captions events every time a video is loaded, depending on what it remembers of a user's preferences,
      // but that's not as useful to us for tracking purposes as knowing when someone actually toggles the captions, so:
      const captionsBtn = this.$el.querySelector('button[data-plyr="captions"]')
      captionsBtn.addEventListener('click', (e) => {
        window._paq.push(['trackEvent', 'video-event', `captions ${this.player.captions.active ? 'enabled' : 'disabled'}`, this.player.media.currentSrc])
      })
    })
  },

  beforeDestroy() {
    this.player.destroy()
  }
}

</script>
